<template>
  <AiEducationLayout :menu-items="menuItems">
    <EducationPageHeader title="学习报告" description="查看当前学生的真实学习汇总数据" />
    <ContentWrap>
      <EducationState v-if="state === 'loading'" state="loading" title="正在加载学习报告" />
      <EducationState
        v-else-if="state === 'error'"
        state="error"
        title="学习报告加载失败"
        description="请稍后重试。"
        @retry="load"
      />
      <EducationState
        v-else-if="!summary"
        state="empty"
        title="暂无学习数据"
        description="完成课程学习后，这里会显示你的学习报告。"
      />
      <template v-else>
        <el-row :gutter="16">
          <el-col v-for="card in cards" :key="card.label" :xs="12" :sm="8" :lg="4">
            <div class="metric-card"
              ><span>{{ card.label }}</span
              ><strong>{{ card.value }}</strong></div
            >
          </el-col>
        </el-row>
        <div class="report-note"
          >数据来自当前学生工作台授权范围，AI 拒答率仅用于学习服务质量分析。</div
        >
      </template>
    </ContentWrap>
  </AiEducationLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import AiEducationLayout from '../layout/AiEducationLayout.vue'
import type { EducationMenuItem } from '../layout/types'
import EducationPageHeader from '../components/EducationPageHeader.vue'
import EducationState from '../components/EducationState.vue'
import { getMyStudentSummary, type StudentSummaryVO } from '@/api/aiEducation'

defineOptions({ name: 'AiEducationStudentLearningReport' })
const summary = ref<StudentSummaryVO | null>(null)
const state = ref<'loading' | 'ready' | 'error'>('loading')
const menuItems: EducationMenuItem[] = [
  {
    title: '学习首页',
    path: '/ai-Application-center/ai-education/student/home',
    icon: 'ep:home-filled'
  },
  {
    title: '我的课程',
    path: '/ai-Application-center/ai-education/student/courses',
    icon: 'ep:reading'
  },
  {
    title: '我的作业',
    path: '/ai-Application-center/ai-education/student/assignments',
    icon: 'ep:document-checked'
  },
  {
    title: '学习计划',
    path: '/ai-Application-center/ai-education/student/learning-plans',
    icon: 'ep:calendar'
  },
  {
    title: '学习报告',
    path: '/ai-Application-center/ai-education/student/learning-report',
    icon: 'ep:data-analysis'
  },
  {
    title: 'AI 学伴',
    path: '/ai-Application-center/ai-education/student/assistant',
    icon: 'ep:magic-stick'
  }
]
const cards = computed(() => {
  if (!summary.value) return []
  const s = summary.value
  return [
    { label: '学习时长', value: `${Math.round(s.studySeconds / 60)} 分钟` },
    { label: '完成章节', value: s.completedChapters },
    { label: '作业提交', value: s.assignmentsSubmitted },
    { label: '答题正确率', value: `${Math.round(s.correctRate * 100)}%` },
    { label: '答题次数', value: s.quizAttempts },
    { label: 'AI 使用次数', value: s.aiRequests }
  ]
})
const load = async () => {
  state.value = 'loading'
  try {
    summary.value = await getMyStudentSummary()
    state.value = 'ready'
  } catch {
    state.value = 'error'
  }
}
onMounted(load)
</script>

<style scoped>
.metric-card {
  min-height: 100px;
  margin-bottom: 16px;
  padding: 18px;
  border: 1px solid #e5e9ec;
  border-radius: 8px;
  background: #fff;
}
.metric-card span {
  display: block;
  color: #697681;
  font-size: 13px;
}
.metric-card strong {
  display: block;
  margin-top: 12px;
  color: #19705d;
  font-size: 24px;
}
.report-note {
  color: #87939d;
  font-size: 13px;
}
</style>
